import { useState, useEffect, useRef } from "react";
import { Shield, Monitor, Smartphone, Tablet, Globe, Clock, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiCall } from "../App";
import { toast } from "sonner";

const DEVICE_ICONS = { Windows: Monitor, Mac: Monitor, Linux: Monitor, Mobile: Smartphone, Tablet: Tablet, Unknown: Globe };

const formatTime = (iso) => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const SessionDropdown = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchSessions = () => {
    if (!token) return;
    apiCall("GET", "/auth/sessions", null, token).then(r => {
      if (r.success) {
        setSessions(r.data.sessions || []);
        setCurrentSid(r.data.current_session_id || null);
      }
    });
  };

  useEffect(() => { fetchSessions(); }, [token]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const revokeSession = async (sid) => {
    const res = await apiCall("POST", `/auth/sessions/revoke/${sid}`, null, token);
    if (res.success) { toast.success("Session revoked"); fetchSessions(); }
    else toast.error(res.error || "Failed");
  };

  const revokeAll = async () => {
    const res = await apiCall("POST", "/auth/sessions/revoke-all", null, token);
    if (res.success) { toast.success(res.data.message); fetchSessions(); }
    else toast.error(res.error || "Failed");
  };

  const otherCount = sessions.filter(s => s.session_id !== currentSid).length;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(!open); if (!open) fetchSessions(); }}
        className="relative p-2 rounded-lg hover:bg-divine-gold/10 transition-all" data-testid="session-dropdown-btn">
        <Shield className={`w-5 h-5 ${otherCount > 0 ? "text-divine-gold" : "text-chakra-crown/40"}`} />
        {otherCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full text-[10px] font-bold bg-amber-500 text-cosmic-void"
            style={{ minWidth: "18px", height: "18px" }} data-testid="session-other-count">
            {otherCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 max-h-[360px] overflow-y-auto rounded-xl border border-divine-gold/20 bg-cosmic-void shadow-2xl z-50" data-testid="session-dropdown">
          <div className="sticky top-0 bg-cosmic-void border-b border-divine-gold/10 px-4 py-3 flex items-center justify-between">
            <span className="font-heading text-sm text-divine-gold">Active Sessions</span>
            <span className="text-chakra-crown/40 text-xs">{sessions.length} device{sessions.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="divide-y divide-divine-gold/10">
            {sessions.map(s => {
              const DeviceIcon = DEVICE_ICONS[s.device] || Globe;
              const isCurrent = s.session_id === currentSid;
              return (
                <div key={s.session_id} className="px-3 py-2.5 hover:bg-divine-gold/5 transition-all group" data-testid={`session-item-${s.session_id}`}>
                  <div className="flex items-center gap-2.5">
                    <DeviceIcon className={`w-4 h-4 shrink-0 ${isCurrent ? "text-divine-gold" : "text-chakra-crown/30"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-chakra-crown/80 text-xs">{s.browser} on {s.device}</span>
                        {isCurrent && <span className="px-1 py-0.5 rounded text-[9px] bg-emerald-500/20 text-emerald-400 font-heading leading-none">THIS</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Clock className="w-2.5 h-2.5 text-chakra-crown/20" />
                        <span className="text-chakra-crown/30 text-[10px]">{formatTime(s.last_active)}</span>
                      </div>
                    </div>
                    {!isCurrent && (
                      <button onClick={() => revokeSession(s.session_id)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 transition-all shrink-0"
                        data-testid={`revoke-${s.session_id}`}>
                        <X className="w-3.5 h-3.5 text-red-400/70" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {otherCount > 0 && (
            <div className="sticky bottom-0 bg-cosmic-void border-t border-divine-gold/10 p-2">
              <Button size="sm" variant="outline" className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs h-7"
                onClick={revokeAll} data-testid="logout-other-sessions-btn">
                <LogOut className="w-3 h-3 mr-1.5" /> Logout {otherCount} Other Session{otherCount !== 1 ? "s" : ""}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionDropdown;
